/**
 * useAnalyticsSection — данные раздела «Аналитики» для секции-виджета.
 *
 * Единая точка загрузки вместо копии useState/useEffect в каждой секции:
 *  - внутри витрины «Все» (AnalyticsAllProvider) слайс берётся из общего
 *    ответа `/analytics/all` — своего запроса секция не делает;
 *  - слайса в общем ответе нет (`null`, сейчас так с `snp_needs`) или
 *    секция открыта отдельным подразделом — грузит свой эндпоинт;
 *  - демо-КАТО (`isHardKato`) — запроса нет вообще, секция рисует моки
 *    hardData.
 *
 * `loading` — сигнал показать каркас-скелет (BlockLoader) вместо виджетов.
 */
import { useEffect, useState } from 'react';

import {
  useAnalyticsAll,
  type AnalyticsSectionKey,
} from './AnalyticsAllContext';
import { useKato } from '../../contexts/KatoContext';
import { useYear } from '../../contexts/YearContext';
import { isHardDataKato } from '../../hardData/constants';

interface Result<T> {
  /** Данные раздела; null — не загружены / нет в ответе / демо-КАТО. */
  data: T | null;
  /** true — раздел ещё грузится: секция показывает каркас-скелет. */
  loading: boolean;
  /** Демо-КАТО (или территория не выбрана) — секция берёт моки hardData. */
  isHardKato: boolean;
}

export const useAnalyticsSection = <T>(
  /** Ключ слайса в ответе `/analytics/all`. */
  key: AnalyticsSectionKey,
  /** Запрос своего эндпоинта — фоллбек вне витрины «Все». */
  fetcher: (kato: number, year?: number | null) => Promise<T>,
): Result<T> => {
  const { selectedKato } = useKato();
  const { selectedYear } = useYear();
  const all = useAnalyticsAll();

  const isHardKato = isHardDataKato(selectedKato);

  // Слайс из общего ответа: пока запрос в полёте — ждём его, не дублируя.
  const fromAll = (all.data?.[key] ?? null) as T | null;
  const useAllResponse = all.enabled && (all.loading || fromAll !== null);

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setData(null);
    if (isHardKato || selectedKato === null || useAllResponse) return;
    let cancelled = false;
    setLoading(true);
    fetcher(selectedKato, selectedYear)
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch((err) => {
        // Раздел без данных рисует каркас с «нет данных», а не ошибку UI.
        if (!cancelled) {
          console.error(`useAnalyticsSection(${key}) failed`, err);
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // fetcher — стабильная функция модуля, в зависимости не нужна.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, selectedKato, isHardKato, selectedYear, useAllResponse]);

  if (useAllResponse) {
    return { data: fromAll, loading: all.loading, isHardKato };
  }
  return { data, loading, isHardKato };
};
